import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import type { AdminUserSummary, Project, StandardGroup } from "../../lib/api";
import {
  createAdminUser,
  fetchProjects,
  fetchUserGrants,
  fetchUsers,
  STANDARD_GROUPS,
  setUserGrants,
  setUserGroups,
} from "../../lib/api";
import { errorMessage } from "../../lib/errorMessage";

const inputClass =
  "rounded-lg border border-adriatic/25 bg-white px-3 py-2 text-sm text-adriatic-deep placeholder:text-adriatic-mid/50 focus:border-acqua focus:ring-2 focus:ring-acqua/30 focus:outline-none disabled:opacity-60";

// admin and superuser reach every project by carrying their own name in the
// roles claim (docs/INTEGRATION.md, rule five); a grants editor for them
// would edit rows nothing downstream ever reads.
const BLANKET_GROUPS = new Set(["admin", "superuser"]);

function GrantsEditor({ user, projects }: { user: AdminUserSummary; projects: Project[] }) {
  const queryClient = useQueryClient();
  const grants = useQuery({
    queryKey: ["admin", "grants", user.userId],
    queryFn: () => fetchUserGrants(user.userId),
  });
  const [selected, setSelected] = useState<Set<string> | null>(null);

  const save = useMutation({
    mutationFn: (next: string[]) => setUserGrants(user.userId, next),
    onSuccess: (next) => {
      queryClient.setQueryData(["admin", "grants", user.userId], next);
      setSelected(null);
    },
  });

  if (grants.isPending) return <p className="text-xs text-adriatic-mid">Loading access...</p>;
  if (grants.isError) {
    return (
      <p role="alert" className="text-xs text-coral-ink">
        {errorMessage(grants.error)}
      </p>
    );
  }

  const current = selected ?? new Set(grants.data);
  const dirty = selected !== null;

  function toggle(slug: string) {
    const next = new Set(current);
    if (next.has(slug)) next.delete(slug);
    else next.add(slug);
    setSelected(next);
  }

  return (
    <div className="mt-2 rounded-lg bg-adriatic-deep/5 p-3">
      {projects.length === 0 ? (
        <p className="text-xs text-adriatic-mid">No projects in the catalog yet.</p>
      ) : (
        <div className="flex flex-wrap gap-3">
          {projects.map((project) => (
            <label
              key={project.slug}
              className="flex items-center gap-1.5 text-xs text-adriatic-deep"
            >
              <input
                type="checkbox"
                checked={current.has(project.slug)}
                onChange={() => toggle(project.slug)}
                className="rounded border-adriatic/40"
              />
              {project.slug}
            </label>
          ))}
        </div>
      )}
      {dirty && (
        <div className="mt-3 flex items-center gap-3">
          <button
            type="button"
            onClick={() => save.mutate(Array.from(current))}
            disabled={save.isPending}
            className="rounded-full bg-coral px-3 py-1 text-xs font-semibold text-white transition-colors hover:bg-coral-light hover:text-adriatic-deep disabled:opacity-50"
          >
            {save.isPending ? "Saving..." : "Save access"}
          </button>
          <button
            type="button"
            onClick={() => setSelected(null)}
            className="text-xs text-adriatic-mid hover:underline"
          >
            Cancel
          </button>
        </div>
      )}
      {errorMessage(save.error) && (
        <p role="alert" className="mt-2 text-xs text-coral-ink">
          {errorMessage(save.error)}
        </p>
      )}
    </div>
  );
}

/**
 * Everybody in the pool, their group, and — for user and guests, the two
 * groups that start with nothing — which projects they can individually
 * reach.
 */
export function UsersPanel() {
  const queryClient = useQueryClient();
  const users = useQuery({ queryKey: ["admin", "users"], queryFn: fetchUsers });
  const projects = useQuery({ queryKey: ["admin", "projects"], queryFn: fetchProjects });

  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [group, setGroup] = useState<StandardGroup>("user");
  const [expanded, setExpanded] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      createAdminUser({ email: email.trim().toLowerCase(), name: name || null, groups: [group] }),
    onSuccess: () => {
      setEmail("");
      setName("");
      setGroup("user");
      queryClient.invalidateQueries({ queryKey: ["admin"] });
    },
  });

  const changeGroup = useMutation({
    mutationFn: ({ userId, groups }: { userId: string; groups: string[] }) =>
      setUserGroups(userId, groups),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["admin"] }),
  });

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!create.isPending && email) create.mutate();
  }

  const isLoading = users.isPending || projects.isPending;
  const loadError = users.isError ? users.error : projects.isError ? projects.error : null;
  const catalog = projects.data ?? [];

  return (
    <section className="rounded-2xl bg-white p-6 shadow-sm">
      <h2 className="text-base font-semibold text-adriatic-deep">People</h2>
      <p className="mt-1 text-sm text-adriatic-mid">
        admin and superuser reach every project. user and guests reach nothing until granted access
        below.
      </p>

      <form onSubmit={onSubmit} className="mt-4 flex flex-wrap items-end gap-3" noValidate>
        <div className="space-y-1">
          <label htmlFor="user-email" className="block text-xs font-medium text-adriatic-deep">
            Work email
          </label>
          <input
            id="user-email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="you@acquire.ai"
            required
            disabled={create.isPending}
            className={`${inputClass} w-56`}
          />
        </div>
        <div className="space-y-1">
          <label htmlFor="user-name" className="block text-xs font-medium text-adriatic-deep">
            Name
          </label>
          <input
            id="user-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Optional"
            disabled={create.isPending}
            className={`${inputClass} w-44`}
          />
        </div>
        <div className="space-y-1">
          <label htmlFor="user-group" className="block text-xs font-medium text-adriatic-deep">
            Group
          </label>
          <select
            id="user-group"
            value={group}
            onChange={(event) => setGroup(event.target.value as StandardGroup)}
            disabled={create.isPending}
            className={`${inputClass} w-32`}
          >
            {STANDARD_GROUPS.map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </select>
        </div>
        <button
          type="submit"
          disabled={create.isPending || !email}
          className="rounded-full bg-coral px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-coral-light hover:text-adriatic-deep focus-visible:ring-2 focus-visible:ring-acqua/50 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
        >
          {create.isPending ? "Creating..." : "Create person"}
        </button>
      </form>
      {errorMessage(create.error) && (
        <p role="alert" className="mt-2 text-sm text-coral-ink">
          {errorMessage(create.error)}
        </p>
      )}

      <div className="mt-5 space-y-2">
        {isLoading ? (
          <p className="text-sm text-adriatic-mid">Loading...</p>
        ) : loadError ? (
          <p role="alert" className="text-sm text-coral-ink">
            {errorMessage(loadError)}
          </p>
        ) : (users.data ?? []).length === 0 ? (
          <p className="text-sm text-adriatic-mid">Nobody has been created yet.</p>
        ) : (
          (users.data ?? []).map((user) => {
            const currentGroup = user.groups[0] ?? "user";
            const canGrant = !BLANKET_GROUPS.has(currentGroup);
            return (
              <div key={user.userId} className="rounded-lg border border-adriatic/10 p-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-adriatic-deep">
                      {user.name ?? user.email}
                    </p>
                    {user.name && <p className="text-xs text-adriatic-mid">{user.email}</p>}
                  </div>
                  <div className="flex items-center gap-3">
                    <select
                      value={currentGroup}
                      onChange={(event) =>
                        changeGroup.mutate({ userId: user.userId, groups: [event.target.value] })
                      }
                      disabled={changeGroup.isPending}
                      className={`${inputClass} w-32`}
                    >
                      {STANDARD_GROUPS.map((g) => (
                        <option key={g} value={g}>
                          {g}
                        </option>
                      ))}
                    </select>
                    {canGrant && (
                      <button
                        type="button"
                        onClick={() => setExpanded(expanded === user.userId ? null : user.userId)}
                        className="text-xs font-medium text-adriatic-deep hover:underline"
                      >
                        {expanded === user.userId ? "Hide access" : "Edit access"}
                      </button>
                    )}
                  </div>
                </div>
                {canGrant && expanded === user.userId && (
                  <GrantsEditor user={user} projects={catalog} />
                )}
              </div>
            );
          })
        )}
      </div>
      {errorMessage(changeGroup.error) && (
        <p role="alert" className="mt-2 text-sm text-coral-ink">
          {errorMessage(changeGroup.error)}
        </p>
      )}
    </section>
  );
}
